import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Card,
  Avatar,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { recurringExpensesApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useListReturnState } from '../../hooks/useListReturnState';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const rotationLabels = {
  daily: 'روزانه',
  weekly: 'هفتگی',
  'bi-weekly': 'دو هفته‌ای',
  monthly: 'ماهانه',
  quarterly: 'سه ماهه',
  'half-yearly': 'شش ماهه',
  annually: 'سالانه',
};

const RecurringExpenses = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDeleteRecord } = useResourcePermissions('expenses');
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    project_id: 'all',
    user_id: 'all',
    category_id: 'all',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    employees: [],
    projects: [],
    categories: [],
  });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await recurringExpensesApi.getAll(filters);
      if (res?.expenses) {
        setExpenses(res.expenses);
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total || res.expenses.length,
          current: res.pagination?.current_page || 1,
        }));
        if (res.filters) {
          setFilterOptions({
            employees: res.filters.employees || [],
            projects: res.filters.projects || [],
            categories: res.filters.categories || [],
          });
        }
      }
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
      message.error('خطا در دریافت لیست هزینه‌های دوره‌ای');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف هزینه دوره‌ای</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف این هزینه دوره‌ای اطمینان دارید؟
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await recurringExpensesApi.delete(id);
          message.success('هزینه دوره‌ای با موفقیت حذف شد');
          fetchExpenses();
        } catch (error) {
          message.error('خطا در حذف هزینه دوره‌ای');
        }
      },
    });
  };

  const handleStatusChange = async (id, checked) => {
    const newStatus = checked ? 'active' : 'inactive';
    setStatusLoadingId(id);
    try {
      await recurringExpensesApi.updateStatus(id, newStatus);
      message.success('وضعیت هزینه دوره‌ای با موفقیت تغییر کرد');
      fetchExpenses();
    } catch (error) {
      message.error('خطا در تغییر وضعیت');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>,
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text) => (
        <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>
          {text}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>,
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(price, record.currency?.currency_symbol)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
      dataIndex: 'user',
      key: 'user',
      render: (user) =>
        user ? (
          <Space>
            <Avatar src={user.image_url} size="small">
              {user.name?.[0]}
            </Avatar>
            <span style={{ fontFamily: 'CustomFont' }}>{user.name}</span>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تناوب</span>,
      dataIndex: 'rotation',
      key: 'rotation',
      render: (rotation) => (
        <Tag color="blue" style={{ fontFamily: 'CustomFont' }}>
          {rotationLabels[rotation] || rotation}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>,
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>هزینه بعدی</span>,
      dataIndex: 'next_expense_date',
      key: 'next_expense_date',
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch
          checked={status === 'active'}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          loading={statusLoadingId === record.id}
          checkedChildren="فعال"
          unCheckedChildren="غیرفعال"
          style={{ fontFamily: 'CustomFont' }}
        />
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigateToDetail(`/finance/recurring-expenses/${record.id}`)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            />
          </Tooltip>
          {canEditRecord(record) ? (
            <Tooltip title="ویرایش">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigateToDetail(`/finance/recurring-expenses/${record.id}/edit`)}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              />
            </Tooltip>
          ) : null}
          {canDeleteRecord(record) ? (
            <Tooltip title="حذف">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                  <SyncOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
                </div>
                <div>
                  <h1
                    className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    هزینه‌های دوره‌ای
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    مدیریت هزینه‌های تکرار شونده سازمان
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchExpenses}
                    size="middle"
                    loading={loading}
                  />
                </Tooltip>
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/finance/recurring-expenses/add')}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ثبت هزینه دوره‌ای جدید
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <>
                {/* Filters */}
                <div className="mb-6 flex flex-wrap gap-4 items-center">
                  <Input
                    placeholder="جستجو..."
                    allowClear
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ fontFamily: 'CustomFont', maxWidth: '250px' }}
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="rounded-lg"
                  />
                  <Select
                    placeholder="وضعیت"
                    value={filters.status === 'all' ? undefined : filters.status}
                    onChange={(value) => handleFilterChange('status', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '130px' }}
                    allowClear
                  >
                    <Option value="active">فعال</Option>
                    <Option value="inactive">غیرفعال</Option>
                  </Select>
                  <Select
                    placeholder="کارمند"
                    value={filters.user_id === 'all' ? undefined : filters.user_id}
                    onChange={(value) => handleFilterChange('user_id', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.employees.map((emp) => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.name}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="پروژه"
                    value={filters.project_id === 'all' ? undefined : filters.project_id}
                    onChange={(value) => handleFilterChange('project_id', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.projects.map((proj) => (
                      <Option key={proj.id} value={proj.id}>
                        {proj.project_name}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="دسته‌بندی"
                    value={filters.category_id === 'all' ? undefined : filters.category_id}
                    onChange={(value) => handleFilterChange('category_id', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.categories.map((cat) => (
                      <Option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Table */}
                <Card className="dark:bg-gray-800 border-0 shadow-sm">
                  <Table
                    columns={columns}
                    dataSource={expenses}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total) => (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          مجموع: {toPersianNumber(total)} هزینه دوره‌ای
                        </span>
                      ),
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                  />
                </Card>
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RecurringExpenses;
